<template>
  <section class="financial-conciliation-toolbar app-filter-toolbar">
    <div
      class="toolbar-main-row app-filter-toolbar-row financial-conciliation-toolbar-row"
      :class="{ 'financial-conciliation-toolbar-row--open': showFilters }"
    >
      <div class="search-wrap app-filter-search-wrap">
        <i class="fa fa-search" aria-hidden="true"></i>
        <input
          v-model="search"
          type="text"
          :placeholder="labels.searchPlaceholder"
          @input="emitSearch"
          @keyup.enter="emitApply"
        />
      </div>

      <div class="toolbar-right app-filter-toolbar-right">
        <AppFilterDateRange
          v-model="dateRangeValue"
          class="app-filter-date-inline"
          label=""
          :show-input="showDateInput"
          :presets="labels.dateRangePresets"
          initial-preset-key="this_month"
          @change="onDateRangeChange"
        />

        <multiselect
          v-if="inlineFilterConfig"
          v-model="filters[inlineFilterConfig.key]"
          :options="options[inlineFilterConfig.key]"
          track-by="value"
          label="label"
          :multiple="true"
          :close-on-select="false"
          :clear-on-select="false"
          :preserve-search="true"
          :show-labels="false"
          :searchable="inlineFilterConfig.searchable"
          :allow-empty="true"
          :max-height="220"
          :placeholder="inlineFilterConfig.placeholder"
          class="financial-conciliation-toolbar-inline app-filter-select app-select app-select--filters"
          @select="onSelect(inlineFilterConfig.key, $event)"
          @remove="onRemove(inlineFilterConfig.key)"
        >
          <template #selection="{ values, isOpen }">
            <span v-if="!isOpen" class="multiselect__single">
              {{ selectionLabel(inlineFilterConfig.key, values) }}
            </span>
          </template>
          <template #noResult>
            <span>{{ labels.emptyOption }}</span>
          </template>
          <template #noOptions>
            <span>{{ labels.emptyOption }}</span>
          </template>
        </multiselect>

        <button
          type="button"
          class="icon-btn app-filter-icon-btn"
          :class="{ active: showFilters }"
          :title="labels.filtersButtonTitle"
          @click="toggleFilters"
        >
          <i class="fa fa-filter" aria-hidden="true"></i>
        </button>

        <button
          type="button"
          class="icon-btn app-filter-icon-btn financial-conciliation-toolbar-search-btn"
          :title="labels.searchButtonTitle"
          @click="emitApply"
        >
          <i class="fa fa-search" aria-hidden="true"></i>
        </button>
      </div>
    </div>

    <transition name="filters-expand" @after-leave="handleFiltersAfterLeave">
      <div v-show="showFilters" class="filters-panel app-filter-panel">
        <div class="financial-conciliation-filters-grid">
          <template v-if="isUnassignedTab">
            <div
              v-for="filter in unassignedFilterConfigs"
              :key="filter.key"
              class="filter-field app-filter-field"
            >
              <span>{{ filter.label }}</span>
              <multiselect
                v-model="filters[filter.key]"
                :options="options[filter.key]"
                track-by="value"
                label="label"
                :multiple="true"
                :close-on-select="false"
                :clear-on-select="false"
                :preserve-search="true"
                :show-labels="false"
                :searchable="filter.searchable"
                :allow-empty="true"
                :max-height="220"
                :placeholder="filter.placeholder"
                class="app-filter-select app-select app-select--filters"
                @select="onSelect(filter.key, $event)"
                @remove="onRemove(filter.key)"
              >
                <template #selection="{ values, isOpen }">
                  <span v-if="!isOpen" class="multiselect__single">
                    {{ selectionLabel(filter.key, values) }}
                  </span>
                </template>
                <template #noResult>
                  <span>{{ labels.emptyOption }}</span>
                </template>
                <template #noOptions>
                  <span>{{ labels.emptyOption }}</span>
                </template>
              </multiselect>
            </div>
          </template>

          <template v-else>
            <div class="filter-field app-filter-field">
              <span>{{ labels.filters.movementType.label }}</span>
              <multiselect
                v-model="selectedMovementType"
                :options="movementTypeOptions"
                track-by="value"
                label="label"
                :multiple="false"
                :close-on-select="true"
                :clear-on-select="false"
                :preserve-search="true"
                :show-labels="false"
                :searchable="false"
                :allow-empty="false"
                :max-height="220"
                :placeholder="labels.filters.movementType.placeholder"
                class="app-filter-select app-select app-select--filters"
              >
                <template #noResult>
                  <span>{{ labels.emptyOption }}</span>
                </template>
                <template #noOptions>
                  <span>{{ labels.emptyOption }}</span>
                </template>
              </multiselect>
            </div>

            <div class="filter-field app-filter-field">
              <span>{{ labels.filters.type.label }}</span>
              <multiselect
                v-model="selectedType"
                :options="typeOptions"
                track-by="value"
                label="label"
                :multiple="false"
                :close-on-select="true"
                :clear-on-select="false"
                :preserve-search="true"
                :show-labels="false"
                :searchable="false"
                :allow-empty="false"
                :max-height="220"
                :placeholder="labels.filters.type.placeholder"
                class="app-filter-select app-select app-select--filters"
              >
                <template #noResult>
                  <span>{{ labels.emptyOption }}</span>
                </template>
                <template #noOptions>
                  <span>{{ labels.emptyOption }}</span>
                </template>
              </multiselect>
            </div>
          </template>
        </div>
      </div>
    </transition>
  </section>
</template>

<script>
import AppFilterDateRange from '@/components/common/AppFilterDateRange.vue';
import {
  ALL_OPTION_VALUE,
  applyRemoveOption,
  applySelectOption,
  buildSelectedValues,
  ensureDefaultSelection,
} from '@/utils/filterSelectors';
import {
  buildToolbarApplyPayload,
  buildToolbarSelectionLabel,
  createToolbarAllOption,
  extractToolbarDateRange,
  mapToolbarOptions,
  resolveToolbarToggleState,
  shouldEmitToolbarDateChange,
} from '@/composables/shared/useFilterToolbarShared';
import { buildCurrentMonthDateRange } from '@/utils/datePresentation';

const MULTI_FILTER_KEYS = ['customer', 'account', 'supplier', 'collaborator'];

const normalizeSimpleOptions = (rows) => {
  const source = rows && typeof rows === 'object' ? rows : {};

  return Object.keys(source)
    .map((key) => ({ value: key, label: String(source[key] || '').trim() }))
    .filter((item) => item.value !== '' && item.label !== '');
};

export default {
  name: 'FinancialConciliationToolbar',
  components: {
    AppFilterDateRange,
  },
  props: {
    labels: {
      type: Object,
      required: true,
    },
    filterOptions: {
      type: Object,
      default: () => ({}),
    },
    activeTab: {
      type: String,
      default: 'unassigned',
    },
  },
  emits: ['search-change', 'filters-change', 'apply'],
  data() {
    const options = {};
    const filters = {};

    MULTI_FILTER_KEYS.forEach((key) => {
      const allOption = this.createAllOptionForKey(key);
      options[key] = [allOption];
      filters[key] = [allOption];
    });

    return {
      search: '',
      showFilters: false,
      showDateInput: false,
      dateRangeValue: buildCurrentMonthDateRange(),
      options,
      filters,
      selectedMovementTypeValue: 'all',
      selectedTypeValue: 'all',
    };
  },
  computed: {
    isUnassignedTab() {
      return String(this.activeTab || '').trim() !== 'assigned';
    },
    inlineFilterConfig() {
      if (this.isUnassignedTab) {
        return this.filterConfigByKey('customer');
      }

      return this.filterConfigByKey('account');
    },
    unassignedFilterConfigs() {
      return ['supplier', 'collaborator']
        .map((key) => this.filterConfigByKey(key))
        .filter(Boolean);
    },
    movementTypeOptions() {
      const labels = this.labels?.filters?.movementType?.options || {};
      return normalizeSimpleOptions(labels);
    },
    typeOptions() {
      const labels = this.labels?.filters?.type?.options || {};
      return normalizeSimpleOptions(labels);
    },
    selectedMovementType: {
      get() {
        return this.movementTypeOptions.find((option) => option.value === this.selectedMovementTypeValue)
          || this.movementTypeOptions[0]
          || null;
      },
      set(option) {
        this.selectedMovementTypeValue = String(option?.value || 'all').trim() || 'all';
      },
    },
    selectedType: {
      get() {
        return this.typeOptions.find((option) => option.value === this.selectedTypeValue)
          || this.typeOptions[0]
          || null;
      },
      set(option) {
        this.selectedTypeValue = String(option?.value || 'all').trim() || 'all';
      },
    },
  },
  watch: {
    filterOptions: {
      deep: true,
      immediate: true,
      handler() {
        this.syncOptionsFromProps();
      },
    },
  },
  methods: {
    filterConfigByKey(key) {
      const config = this.labels?.filters?.[key] || {};
      return {
        key,
        label: String(config.label || '').trim(),
        placeholder: String(config.placeholder || '').trim(),
        searchable: Boolean(config.searchable),
      };
    },
    createAllOptionForKey(key) {
      return createToolbarAllOption(this.labels?.allOptionLabels, key);
    },
    toggleFilters() {
      const nextState = resolveToolbarToggleState(this.showFilters);
      this.showFilters = nextState.showFilters;

      if (nextState.showDateInput !== null) {
        this.showDateInput = nextState.showDateInput;
      }
    },
    handleFiltersAfterLeave() {
      this.showDateInput = false;
    },
    onDateRangeChange(payload) {
      if (!shouldEmitToolbarDateChange(payload, this.dateRangeValue)) {
        return;
      }

      this.emitApply();
    },
    onSelect(filterKey, option) {
      this.filters[filterKey] = applySelectOption(
        this.filters[filterKey],
        option,
        this.createAllOptionForKey(filterKey)
      );
    },
    onRemove(filterKey) {
      this.filters[filterKey] = applyRemoveOption(
        this.filters[filterKey],
        this.createAllOptionForKey(filterKey)
      );
    },
    selectionLabel(filterKey, values) {
      const selected = Array.isArray(values) ? values : [];
      const hasAllOption = selected.some((item) => item?.value === ALL_OPTION_VALUE);
      const customAllLabel = String(this.labels?.selectionAllLabels?.[filterKey] || '').trim();

      if (hasAllOption && customAllLabel !== '') {
        return buildToolbarSelectionLabel(values, customAllLabel);
      }

      if (selected.length === 0 && customAllLabel !== '') {
        return buildToolbarSelectionLabel(values, customAllLabel);
      }

      return buildToolbarSelectionLabel(values);
    },
    emitSearch() {
      this.$emit('search-change', this.search);
    },
    extractDateRange() {
      return extractToolbarDateRange(this.dateRangeValue, {
        startKey: 'date_from',
        endKey: 'date_to',
      });
    },
    emitFilters() {
      const payload = {
        ...this.extractDateRange(),
        customer_ids: buildSelectedValues(this.filters.customer),
        financial_account_ids: buildSelectedValues(this.filters.account),
        supplier_ids: buildSelectedValues(this.filters.supplier),
        collaborator_ids: buildSelectedValues(this.filters.collaborator),
        movement_type: String(this.selectedMovementTypeValue || 'all').trim() || 'all',
        type: String(this.selectedTypeValue || 'all').trim() || 'all',
      };

      this.$emit('filters-change', payload);
      return payload;
    },
    emitApply() {
      this.emitSearch();
      const filters = this.emitFilters();
      this.$emit('apply', buildToolbarApplyPayload(this.search, filters));
    },
    syncOptionsFromProps() {
      const nextOptions = {};
      const nextFilters = {};

      MULTI_FILTER_KEYS.forEach((key) => {
        const allOption = this.createAllOptionForKey(key);
        const list = mapToolbarOptions(this.filterOptions?.[key] || []);
        const allowedValues = new Set([allOption.value, ...list.map((item) => item?.value)]);
        const currentSelection = Array.isArray(this.filters?.[key]) ? this.filters[key] : [];
        const validSelection = currentSelection.filter((item) => allowedValues.has(item?.value));

        nextOptions[key] = [allOption, ...list];
        nextFilters[key] = ensureDefaultSelection(validSelection, allOption);
      });

      this.options = {
        ...this.options,
        ...nextOptions,
      };

      this.filters = {
        ...this.filters,
        ...nextFilters,
      };
    },
  },
};
</script>

<style scoped>
.financial-conciliation-toolbar-row--open {
  align-items: flex-start;
}

.financial-conciliation-toolbar-row--open .toolbar-right {
  align-items: flex-start;
}

.financial-conciliation-toolbar-row--open .toolbar-right > * {
  align-self: flex-start;
}

.financial-conciliation-toolbar-inline {
  --app-filter-inline-width: 238px;
  width: 238px;
  min-width: 238px;
  flex: 0 0 238px;
  margin-top: 4px;
}

.financial-conciliation-toolbar-search-btn {
  background-color: #0070cd;
  border-color: #0070cd;
  color: #ffffff;
}

.financial-conciliation-toolbar-search-btn i {
  color: #ffffff;
}

.financial-conciliation-toolbar-search-btn:hover,
.financial-conciliation-toolbar-search-btn:focus {
  background-color: #005eae;
  border-color: #005eae;
}

.financial-conciliation-filters-grid {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

@media (max-width: 900px) {
  .financial-conciliation-filters-grid {
    grid-template-columns: 1fr;
  }
}
</style>
